/**
 * batch-71 kiosk-magazijn (FO-532, spec 232) - het vaste profiel van een
 * kioskaccount: de ENIGE bron van wat een gedeelde magazijn-tablet mag.
 *
 * Drie lagen die elk afzonderlijk volstaan (spec §14):
 *
 *  a. **Middleware** (`packages/auth/src/middleware.ts`): een kiosksessie opent
 *     alleen `/kiosk/*`; elk ander pad gaat terug naar `/kiosk/magazijn`.
 *  b. **Route-allowlist** (`requireAuth` -> `kioskRouteToegestaan` hieronder):
 *     elke API-route buiten de tabel geeft 403 `KIOSK_SCOPE`, ook een `GET` en ook
 *     bij een directe aanroep met de token.
 *  c. **Module-klem** (`KIOSK_MAGAZIJN_ALLOWLIST`): `getModulePermissions`,
 *     `requirePermission` en `computeEffectivePermissions` kennen een kioskaccount
 *     uitsluitend deze rechten toe, ongeacht de rechtenmatrix.
 *
 * Patroon: `accountant.ts` (FO-404). Eén verschil: de accountant-klem neemt alleen
 * weg en de rijen komen uit de tabel; een kioskaccount HEEFT dit profiel, de tabel
 * kan er niets aan veranderen. Uitbreiden = één wijziging hier plus review, nooit
 * via de rechtenmatrix.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'

// ─── Accountsoort ────────────────────────────────────────────────────────────

export const ACCOUNT_TYPES = ['PERSOON', 'KIOSK'] as const
export type AccountType = (typeof ACCOUNT_TYPES)[number]

export const KIOSK_PROFIELEN = ['MAGAZIJN'] as const
export type KioskProfiel = (typeof KIOSK_PROFIELEN)[number]

/** Is dit een gedeeld kioskaccount? Tolerant voor `null`/`undefined`. */
export function isKioskAccount(accountType: string | null | undefined): boolean {
  return accountType === 'KIOSK'
}

// ─── Foutcodes en headers ────────────────────────────────────────────────────

/** Route of module buiten het vaste profiel (403). */
export const KIOSK_SCOPE_CODE = 'KIOSK_SCOPE'
/**
 * Schrijfactie op de tablet zonder geldige operator (403): geen token, verlopen, of de
 * operator mag niet meer (`details.reden`). De UI toont dan het PIN-scherm.
 */
export const KIOSK_OPERATOR_VEREIST_CODE = 'KIOSK_OPERATOR_VEREIST'
/** Operator heeft geen recht op deze handeling (403): de tegel is verborgen, dit is het API-vangnet. */
export const KIOSK_OPERATOR_GEEN_RECHT_CODE = 'KIOSK_OPERATOR_GEEN_RECHT'
/** Poging de rechten van een kioskaccount te wijzigen (422), zie het override-pad. */
export const KIOSK_ALLOWLIST_CODE = 'KIOSK_ALLOWLIST_LOCKED'
/** De sessie is ingetrokken of ouder dan de maximale duur (401). */
export const KIOSK_SESSIE_CODE = 'SESSIE_INGETROKKEN'

export const KIOSK_SCOPE_MESSAGE =
  'Dit kan niet op de magazijn-tablet. Log in met je eigen account als je dit nodig hebt.'
export const KIOSK_OPERATOR_VEREIST_MESSAGE =
  'Kies eerst wie je bent en voer je PIN in. Daarna kun je verder waar je gebleven was.'
export const KIOSK_ALLOWLIST_MESSAGE =
  'Een kioskaccount heeft een vast profiel: alleen magazijn-handelingen. Rechten zijn niet te wijzigen.'

/** HTTP-header met het operator-token (patroon `IMPERSONATION_HEADER`). */
export const KIOSK_OPERATOR_HEADER = 'x-kiosk-operator'
/** HttpOnly-cookie in de backoffice waaruit `serverApiFetch` de header zet. */
export const KIOSK_OPERATOR_COOKIE = 'streep_kiosk_operator'
/** Welke tablet dit is (instelbaar per browser); geen persoonsgegeven. */
export const KIOSK_DEVICE_COOKIE = 'streep_kiosk_device'
/** Waarde van `X-Client` op requests uit de kiosk-shell: een persoonsaccount is daar zelf de operator. */
export const KIOSK_CLIENT = 'kiosk'
/**
 * HKDF-salt en -info voor de pepper van het kiosk-wachtwoord: de API (hash bij
 * instellen) en de CredentialsProvider in de backoffice (verify bij inloggen) moeten
 * dezelfde afleiding uit `AUTH_SECRET` gebruiken. Geen nieuwe env-variabele.
 */
export const KIOSK_HKDF_SALT = 'streep-kiosk'
export const KIOSK_WACHTWOORD_HKDF_INFO = 'kiosk-wachtwoord-pepper'
/** Geldigheid van een operator-token; bij elke handeling verlengd. */
export const KIOSK_OPERATOR_TOKEN_SECONDEN = 15 * 60
/** Een kiosksessie duurt nooit langer dan dit, ook al blijft de tablet aan (spec §B8). */
export const KIOSK_SESSIE_MAX_SECONDEN = 24 * 60 * 60

// ─── Module-allowlist ────────────────────────────────────────────────────────

export const KIOSK_PERMISSIE_VLAGGEN = [
  'canView',
  'canEdit',
  'canDelete',
  'canApprove',
  'canInvoice',
] as const
export type KioskPermissieVlag = (typeof KIOSK_PERMISSIE_VLAGGEN)[number]

/**
 * De enige modules en vlaggen van een magazijn-kiosk (spec §A2). INKOOP is breder
 * dan ontvangst: de route-allowlist hieronder beperkt het tot de ontvangst-routes.
 */
export const KIOSK_MAGAZIJN_ALLOWLIST: Readonly<Record<string, ReadonlyArray<KioskPermissieVlag>>> = {
  VOORRAAD: ['canView', 'canEdit', 'canApprove'],
  ASSETS: ['canView', 'canEdit'],
  APPARATUUR: ['canView', 'canEdit'],
  UITRUSTING: ['canView', 'canEdit'],
  INKOOP: ['canView', 'canEdit'],
}

/** Mag een magazijn-kiosk deze (module, vlag)-combinatie? */
export function kioskVlagToegestaan(module: string, vlag: string): boolean {
  const toegestaan = KIOSK_MAGAZIJN_ALLOWLIST[module]
  if (!toegestaan) return false
  return (toegestaan as ReadonlyArray<string>).includes(vlag)
}

export interface KioskPermissieRij {
  module: string
  canView: boolean
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  canInvoice: boolean
}

/**
 * Het volledige vaste profiel als permissie-rijen: de vorm van
 * `getModulePermissions`. De `ModulePermission`-tabel speelt voor een kioskaccount
 * geen rol; `computeEffectivePermissions` schrijft precies deze rijen weg.
 */
export function kioskProfielRijen(): KioskPermissieRij[] {
  return Object.entries(KIOSK_MAGAZIJN_ALLOWLIST).map(([module, vlaggen]) => ({
    module,
    canView: vlaggen.includes('canView'),
    canEdit: vlaggen.includes('canEdit'),
    canDelete: vlaggen.includes('canDelete'),
    canApprove: vlaggen.includes('canApprove'),
    canInvoice: vlaggen.includes('canInvoice'),
  }))
}

// ─── Route-allowlist ─────────────────────────────────────────────────────────

/**
 * De magazijn-routefamilies (spec §A2). Alles daarbuiten is altijd dicht; binnen
 * deze families beslist de tabel hieronder per route. `GET /employees` staat hier
 * bewust niet: de tablet leest medewerkers via `GET /kiosk/medewerkers`.
 */
export const KIOSK_MAGAZIJN_ROUTE_PREFIXES = [
  '/voorraad',
  '/karren',
  '/koffer-sets',
  '/samenstellingen',
  '/inname-sessies',
  '/inname-sessie-items',
  '/middelen',
  '/uitrusting/leveringen',
  '/inkoop/ontvangsten',
  '/inkoop/orders/:id/ontvangsten',
  '/me',
  '/kiosk',
] as const

/**
 * Elke route onder de families is hier expliciet gemarkeerd: `true` = de tablet mag
 * hem aanroepen, `false` = bewust dicht. Sleutel = `METHODE /pad` met het
 * route-patroon zonder `/api/v1`. Een route die hier ontbreekt is dicht en laat de
 * drift-test `kiosk-route-allowlist.test.ts` falen, zodat een nieuwe magazijn-route
 * een bewuste keuze is.
 *
 * Bewust dicht zijn o.a. alles met een inkoopprijs (`/voorraad/artikelen*`,
 * `/voorraad/locaties`: de tablet heeft prijsloze `/kiosk/artikelen`), PIN/PUK
 * (`reveal`), beheer (locaties, modules, templates, instellingen), rapportages en
 * exports, en HR-beoordeling van innames.
 *
 * `true` = de schermen van de tablet gebruiken hem, of de spec noemt hem met name
 * (`/middelen/innemen`, `/overdragen`, `/tijdlijn`). Al het andere staat dicht, ook
 * onschuldige routes: wat niet nodig is, staat niet open. Dicht dus ook de bulk-uitgifte en
 * -retour zonder formulier (die slaan handtekening en bon over) en de uitrusting-leveringen
 * (geen scherm, geen operatorveld). Een nieuw scherm zet hier zijn routes open, met review.
 */
export const KIOSK_MAGAZIJN_ROUTES: Readonly<Record<string, boolean>> = {
  // ── Eigen context ─────────────────────────────────────────────────────────
  'GET /me': true,
  'GET /me/permissions': true,

  // ── Scannen en context ────────────────────────────────────────────────────
  'GET /voorraad/qr/resolve': true,
  'GET /voorraad/qr/image': false,
  'POST /voorraad/qr/pdf': true,
  'POST /voorraad/qr/backfill': false,
  'GET /voorraad/qr/labels': false,
  'POST /voorraad/qr/labels': false,
  'GET /voorraad/qr/labels/series': false,
  'POST /voorraad/qr/labels/:token/koppel': false,
  'POST /voorraad/qr/labels/:token/koppel-sleutel': false,
  'POST /voorraad/qr/labels/:token/vervallen': false,
  'GET /voorraad/qr/sleutel-koppel-context': false,
  'GET /voorraad/qr/sleutel-koppel-voertuigen': false,
  'GET /voorraad/portal/artikelen': false,
  'GET /voorraad/portal/locaties': true,
  'GET /voorraad/vaklocaties': false,
  'GET /voorraad/locaties/:id/indeling': false,
  'POST /voorraad/locaties/:id/vaklocaties': false,

  // ── Boekingen ─────────────────────────────────────────────────────────────
  'POST /voorraad/mutaties/uitgifte': false,
  'POST /voorraad/mutaties/inkoop': true,
  'POST /voorraad/mutaties/retour': false,
  'POST /voorraad/mutaties/afboeking': true,
  'POST /voorraad/mutaties/verplaatsing': true,
  'POST /voorraad/mutaties/correctie': true,
  'POST /voorraad/mutaties/portal': false,
  'GET /voorraad/mutaties': false,
  'PATCH /voorraad/artikelen/:id/vaklocatie': true,
  'GET /voorraad/artikelen/:id/verschillen': true,
  'POST /voorraad/artikelen/:id/verschillen/meenemen-bij-ontvangst': false,
  'GET /voorraad/artikelen/:id/foto': false,
  'GET /voorraad/verschillen': false,
  'POST /voorraad/verschillen/:id/afwikkelen': false,

  // ── Uniek uitgeven, innemen, verplaatsen, status ──────────────────────────
  'POST /voorraad/uniek/:id/uitgeven': false,
  'POST /voorraad/uniek/:id/innemen': false,
  'POST /voorraad/uniek/:id/verplaatsen': true,
  'POST /voorraad/uniek/:id/status': true,
  'GET /voorraad/uniek/koppel-zoek': false,
  'GET /voorraad/uniek/:id/inhoud': false,
  'GET /voorraad/uniek/:id/deksel-pdf': false,
  'POST /voorraad/uniek/:id/inhoud-afwijkingen': false,
  'PATCH /voorraad/uniek/:id/inhoud-afwijkingen/:afwijkingId': false,
  'DELETE /voorraad/uniek/:id/inhoud-afwijkingen/:afwijkingId': false,
  'GET /voorraad/uitgiftes': false,
  'POST /voorraad/uitgiftes/innemen-bulk': false,
  'GET /voorraad/employees/:employeeId/uitrusting': false,
  'POST /voorraad/employees/:employeeId/uitrusting/:uitrustingItemId/innemen': false,
  'GET /voorraad/retourplicht': false,
  'POST /voorraad/retour-meldingen/verwerken': false,

  // ── Telling (tellen op de vloer; doorvoeren en afronden op kantoor) ───────
  'GET /voorraad/tellingen': true,
  'POST /voorraad/tellingen': false,
  'GET /voorraad/tellingen/instellingen': false,
  'PUT /voorraad/tellingen/instellingen': false,
  'POST /voorraad/tellingen/regels/:regelId/beslissing': false,
  'GET /voorraad/tellingen/:id': true,
  'GET /voorraad/tellingen/:id/regels': true,
  'GET /voorraad/tellingen/:id/audit': false,
  'PATCH /voorraad/tellingen/:id/regels/:regelId': true,
  'PUT /voorraad/tellingen/:id/regels/:regelId/reden': false,
  'POST /voorraad/tellingen/:id/scan': true,
  'POST /voorraad/tellingen/:id/hertellen': false,
  'POST /voorraad/tellingen/:id/overslaan': false,
  'POST /voorraad/tellingen/:id/doorvoeren': false,
  'POST /voorraad/tellingen/:id/niet-corrigeren': false,
  'POST /voorraad/tellingen/:id/import': false,
  'POST /voorraad/tellingen/:id/afronden': false,
  'POST /voorraad/tellingen/:id/annuleren': false,

  // ── Bewust dicht: stamdata, prijzen, beheer, rapportage, persoonlijk ──────
  'GET /voorraad/alerts': false,
  'GET /voorraad/artikelen': false,
  'POST /voorraad/artikelen': false,
  'GET /voorraad/artikelen/:id': false,
  'PATCH /voorraad/artikelen/:id': false,
  'DELETE /voorraad/artikelen/:id': false,
  'GET /voorraad/artikelen/:id/beoordeling': false,
  'POST /voorraad/artikelen/:id/beoordeling': false,
  'GET /voorraad/artikelen/:id/conversie-preview': false,
  'POST /voorraad/artikelen/:id/converteer-naar-bulk': false,
  'POST /voorraad/artikelen/:id/foto': false,
  'DELETE /voorraad/artikelen/:id/foto': false,
  'GET /voorraad/artikelen/:id/houders': false,
  'POST /voorraad/artikelen/:id/los-saldo-naar-exemplaren': false,
  'POST /voorraad/artikelen/:id/merge': false,
  'POST /voorraad/artikelen/:id/merge-preview': false,
  'GET /voorraad/artikelen/:id/mutaties': false,
  'POST /voorraad/artikelen/:id/prijsvoorstel': false,
  'GET /voorraad/artikelen/:id/saldi': false,
  'GET /voorraad/artikelen/:id/uniek': false,
  'GET /voorraad/artikelen/:id/uniek-gebeurtenissen': false,
  'PUT /voorraad/artikelen/:id/vaste-inhoud': false,
  'GET /voorraad/duplicaten': false,
  'GET /voorraad/instellingen': false,
  'PUT /voorraad/instellingen/afschrijving': false,
  'POST /voorraad/koffers': false,
  'GET /voorraad/koffers/artikelen': false,
  'POST /voorraad/koffers/uitrol': false,
  'GET /voorraad/locaties': false,
  'POST /voorraad/locaties': false,
  'PATCH /voorraad/locaties/:id': false,
  'DELETE /voorraad/locaties/:id': false,
  'GET /voorraad/mijn-koffer': false,
  'GET /voorraad/mijn-uitrusting': false,
  'GET /voorraad/rapportages': false,
  'GET /voorraad/rapportages/waarde-per-onderdeel': false,
  'GET /voorraad/scanner/employees': false,
  'GET /voorraad/uniek': false,
  'POST /voorraad/uniek': false,
  'GET /voorraad/uniek/:id': false,
  'PATCH /voorraad/uniek/:id': false,
  'DELETE /voorraad/uniek/:id': false,
  'POST /voorraad/uniek/:id/koppel-sim': false,
  'POST /voorraad/uniek/:id/ontkoppel-sim': false,
  'POST /voorraad/uniek/:id/koppel-voertuig': false,
  'POST /voorraad/uniek/:id/reserve-sleutel': false,
  'POST /voorraad/uniek/:id/reveal': false,
  'POST /voorraad/uniek/sleutels/uitrol': false,
  'POST /voorraad/voertuig/:voertuigId/bestuurder-wisselen': false,

  // ── Karren en koffers (inhoud, controle, bijvullen) ───────────────────────
  'GET /karren/dragers': true,
  'GET /karren/dragers/:id': true,
  'GET /karren/dragers-picker': false,
  'GET /karren/dragers/:id/lade-pdf': false,
  'POST /karren/dragers/:id/controles': true,
  'GET /karren/controles': false,
  'GET /karren/controles/:id': true,
  'PATCH /karren/controle-regels/:id': true,
  'POST /karren/controles/:id/snelknop': true,
  'POST /karren/controles/:id/afronden': true,
  'POST /karren/controles/:id/annuleren': false,
  'GET /karren/controles/:id/rapport-pdf': false,
  'POST /karren/inhoud-regels': false,
  'PATCH /karren/inhoud-regels/:id': false,
  'POST /karren/inhoud-regels/:id/naar-voorraad': false,
  'POST /karren/inhoud-regels/:id/uitgeven': false,
  'DELETE /karren/inhoud-regels/:id': false,
  'POST /karren/dragers': false,
  'PATCH /karren/dragers/:id': false,
  'POST /karren/dragers/uitrol': false,
  'GET /karren/dragers/:id/bestellingen': false,
  'GET /karren/dragers/:id/kosten': false,
  'POST /karren/dragers/:id/lades': false,
  'PATCH /karren/lades/:ladeId': false,
  'DELETE /karren/lades/:ladeId': false,
  'GET /karren/controles/:id/bestelvoorstel': false,
  'POST /karren/bestelvoorstel': false,
  'POST /karren/bestellingen/:bestellingId/ontvangen': false,
  'POST /karren/bestel-regels/:id/koppel': false,
  'GET /karren/mijn': false,
  'GET /karren/mijn/:uniekItemId': false,
  'POST /karren/mijn/:uniekItemId/melding': false,
  'GET /karren/modules': false,
  'POST /karren/modules': false,
  'GET /karren/modules/:id': false,
  'PATCH /karren/modules/:id': false,
  'DELETE /karren/modules/:id': false,
  'POST /karren/modules/:id/onderdelen': false,
  'PATCH /karren/module-onderdelen/:id': false,
  'DELETE /karren/module-onderdelen/:id': false,
  'GET /karren/pakket-bronnen': false,
  'GET /karren/pakket-bronnen/:id': false,
  'GET /karren/templates': false,
  'POST /karren/templates': false,
  'GET /karren/templates/:id': false,
  'PATCH /karren/templates/:id': false,
  'DELETE /karren/templates/:id': false,
  'POST /karren/templates/:id/doorvoer': false,
  'POST /karren/templates/:id/doorvoer/preview': false,

  // ── Koffers als set ───────────────────────────────────────────────────────
  'GET /koffer-sets/:dragerId': true,
  'POST /koffer-sets/:dragerId/uitgeven': true,
  'POST /koffer-sets/:dragerId/innemen': true,
  'POST /koffer-sets/:dragerId/bijvullen': true,

  // ── Samenstelopdrachten ───────────────────────────────────────────────────
  'GET /samenstellingen': true,
  'GET /samenstellingen/sets/overzicht': false,
  'GET /samenstellingen/:id': true,
  'GET /samenstellingen/:id/afdruk': false,
  'POST /samenstellingen/:id/openen': true,
  'POST /samenstellingen/:id/scan': true,
  'POST /samenstellingen/:id/pauzeren': false,
  'POST /samenstellingen/:id/hervatten': false,
  'POST /samenstellingen/:id/regels/:regelId/afvinken': true,
  'POST /samenstellingen/:id/regels/:regelId/ongedaan': true,
  'POST /samenstellingen/:id/regels/:regelId/defect': false,
  'POST /samenstellingen/:id/uitgeven': true,
  'POST /samenstellingen': false,
  'POST /samenstellingen/sets': false,
  'GET /samenstellingen/pakket-opties': false,
  'GET /samenstellingen/onboarding/:onboardingId': false,
  'POST /samenstellingen/:id/annuleren': false,
  'POST /samenstellingen/:id/bestellijst-doorzetten': false,
  'POST /samenstellingen/:id/regels/:regelId/uitzondering': false,
  'POST /samenstellingen/:id/regels/:regelId/uitzondering-intrekken': false,

  // ── Innames (FO-516); de beoordeling door HR blijft op kantoor ────────────
  'GET /inname-sessies': true,
  'POST /inname-sessies': true,
  'GET /inname-sessies/locaties': true,
  'GET /inname-sessies/:id': true,
  'POST /inname-sessies/:id/scan': true,
  'POST /inname-sessies/:id/pauzeren': false,
  'POST /inname-sessies/:id/hervatten': false,
  'POST /inname-sessies/:id/items/:itemId/afvinken': true,
  'POST /inname-sessies/:id/items/:itemId/koffer': true,
  'POST /inname-sessies/:id/afsluiten': true,
  'GET /inname-sessies/medewerkers': false,
  'GET /inname-sessies/openstaand': false,
  'GET /inname-sessies/ter-beoordeling': false,
  'POST /inname-sessies/:id/bezwaar-afgehandeld': false,
  'GET /inname-sessie-items/:itemId/sessie': false,
  'POST /inname-sessie-items/:itemId/afboeken': false,
  'POST /inname-sessie-items/:itemId/beoordelen': false,
  'POST /inname-sessie-items/:itemId/goedkeuren': false,
  'POST /inname-sessie-items/:itemId/tweede-akkoord': false,
  'POST /inname-sessie-items/:itemId/verrekend': false,

  // ── Middelen: formulier, innemen, overdragen, tijdlijn; geen dossiers ─────
  'GET /middelen/rechten': true,
  'GET /middelen/beschikbaar': false,
  'GET /middelen/locaties': false,
  'POST /middelen/fotos': true,
  'POST /middelen/formulier': true,
  'GET /middelen/formulier/:sessieId': false,
  'POST /middelen/formulier/:sessieId/bevestiging': false,
  'POST /middelen/innemen': true,
  'POST /middelen/overdragen': true,
  'GET /middelen/tijdlijn/:middelType/:middelId': true,
  'GET /middelen/medewerkers': false,
  'GET /middelen/medewerkers/:employeeId': false,
  'GET /middelen/medewerkers/:employeeId/tijdlijn': false,
  'GET /middelen/mutaties/:mutatieId/fotos/:index': false,
  'GET /middelen/instellingen': false,
  'PUT /middelen/instellingen': false,
  'GET /middelen/toewijsbare-artikelen': false,
  'POST /middelen/toewijzen-zonder-voorraad': false,

  // ── Uitrusting: levering en overdracht ────────────────────────────────────
  'GET /uitrusting/leveringen': false,
  'GET /uitrusting/leveringen/locaties': false,
  'GET /uitrusting/leveringen/:id': false,
  'POST /uitrusting/leveringen/:id/klaar': false,
  'POST /uitrusting/leveringen/:id/overdracht': false,
  'POST /uitrusting/leveringen/:id/niet-verschenen': false,
  'POST /uitrusting/leveringen/:id/plan': false,
  'POST /uitrusting/leveringen/:id/kies-moment': false,

  // ── Inkoop: alleen ontvangst ──────────────────────────────────────────────
  'GET /inkoop/ontvangsten/:id': true,
  'POST /inkoop/ontvangsten/:id/binnenboeken': true,
  'POST /inkoop/orders/:id/ontvangsten': true,

  // ── De kiosk zelf ─────────────────────────────────────────────────────────
  'GET /kiosk/context': true,
  'GET /kiosk/operators': true,
  'POST /kiosk/operator/login': true,
  'POST /kiosk/operator/verleng': true,
  'POST /kiosk/operator/lock': true,
  'GET /kiosk/medewerkers': true,
  'GET /kiosk/medewerkers/:employeeId': true,
  'GET /kiosk/medewerkers/:employeeId/foto': true,
  'GET /kiosk/medewerkers/:employeeId/bezit': true,
  'GET /kiosk/artikelen': true,
  'GET /kiosk/artikelen/:artikelId': true,
  'GET /kiosk/inkooporders': true,
  'GET /kiosk/inkooporders/:orderId': true,
  'GET /kiosk/te-doen': true,
  'GET /kiosk/uniek/:id': true,
  'POST /kiosk/uniek/:id/afschrijven': true,
  'GET /kiosk/bon/:sessieId': true,
  'GET /kiosk/document/:documentId': true,
}

/**
 * Routes onder `/kiosk` die een persoonsaccount gebruikt (beheer, eigen PIN) en
 * die voor een kioskaccount dicht zijn: ze staan niet in de tabel hierboven. De
 * drift-test leest deze prefix om ze niet als "vergeten" te rekenen.
 */
export const KIOSK_BEHEER_PREFIX = '/kiosk/beheer'

/**
 * Schrijfroutes die juist zonder operator moeten kunnen: het inloggen en vergrendelen
 * zelf. Alles wat verder geen GET/HEAD/OPTIONS is, eist een operator (spec §B5).
 */
export const KIOSK_ROUTES_ZONDER_OPERATOR: ReadonlySet<string> = new Set([
  'POST /kiosk/operator/login',
  'POST /kiosk/operator/lock',
])

const API_PREFIX = '/api/v1'

/** Het route-patroon zonder `/api/v1` en zonder afsluitende slash of querystring. */
export function normaliseerRoutePatroon(route: string): string {
  const zonderQuery = route.split('?')[0] ?? route
  const zonderPrefix = zonderQuery.startsWith(`${API_PREFIX}/`) ? zonderQuery.slice(API_PREFIX.length) : zonderQuery
  return zonderPrefix.length > 1 && zonderPrefix.endsWith('/') ? zonderPrefix.slice(0, -1) : zonderPrefix
}

export function kioskRouteSleutel(methode: string, route: string): string {
  return `${methode.toUpperCase()} ${normaliseerRoutePatroon(route)}`
}

/** Valt dit route-patroon onder een magazijn-familie (en dus onder de drift-test)? */
export function valtOnderKioskFamilie(route: string): boolean {
  const pad = normaliseerRoutePatroon(route)
  return KIOSK_MAGAZIJN_ROUTE_PREFIXES.some((p) => pad === p || pad.startsWith(`${p}/`))
}

/**
 * Mag een kioskaccount deze route aanroepen? Default dicht: buiten de families
 * altijd nee, binnen de families alleen wat expliciet `true` staat. HEAD volgt GET.
 * Het argument is het route-PATROON (`request.routeOptions.url`), geen rauwe URL:
 * zo kan een padtruc nooit een andere route laten matchen dan er wordt uitgevoerd.
 */
export function kioskRouteToegestaan(methode: string, route: string): boolean {
  const m = methode.toUpperCase()
  const effectief = m === 'HEAD' ? 'GET' : m
  if (m === 'OPTIONS') return false
  if (!valtOnderKioskFamilie(route)) return false
  return KIOSK_MAGAZIJN_ROUTES[kioskRouteSleutel(effectief, route)] === true
}

/** Schrijft deze aanroep iets, en heeft hij dus een operator nodig? */
export function kioskRouteVereistOperator(methode: string, route: string): boolean {
  const m = methode.toUpperCase()
  if (m === 'GET' || m === 'HEAD' || m === 'OPTIONS') return false
  return !KIOSK_ROUTES_ZONDER_OPERATOR.has(kioskRouteSleutel(m, route))
}

// ─── Weergave ────────────────────────────────────────────────────────────────

/** Achtervoegsel in overzichten: "Thomas Wijbrands (magazijn-tablet)". */
export const KIOSK_WEERGAVE_ACHTERVOEGSEL = 'magazijn-tablet'

export function kioskOperatorWeergave(naam: string): string {
  return `${naam} (${KIOSK_WEERGAVE_ACHTERVOEGSEL})`
}

// ─── Handelingen en rechten op het startscherm ───────────────────────────────

export const KIOSK_TEGELS = [
  'SCANNEN',
  'UITGEVEN',
  'INNEMEN',
  'BIJBOEKEN',
  'AFBOEKEN',
  'VERPLAATSEN',
  'CORRECTIE',
  'SAMENSTELLEN',
  'INNAMES',
  'TELLING',
  'KOFFERS',
  'TE_DOEN',
  'LABELS',
] as const
export type KioskTegel = (typeof KIOSK_TEGELS)[number]

/** Redenen voor een afboeking op de tablet (keuzelijst, verplicht). */
export const KIOSK_AFBOEK_REDENEN = ['KAPOT', 'VERLOREN', 'VERVALLEN', 'VERBRUIK', 'OVERIG'] as const
export type KioskAfboekReden = (typeof KIOSK_AFBOEK_REDENEN)[number]
export const KIOSK_AFBOEK_REDEN_LABELS: Record<KioskAfboekReden, string> = {
  KAPOT: 'Kapot',
  VERLOREN: 'Verloren',
  VERVALLEN: 'Houdbaarheid verstreken',
  VERBRUIK: 'Verbruikt',
  OVERIG: 'Anders, zie de toelichting',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface KioskOperatorDTO {
  employeeId: string
  naam: string
  /** Kortere naam op de tegel. */
  voornaam: string
  heeftFoto: boolean
  /** Nog geen PIN: de tegel legt uit dat je er een instelt in de portal. */
  pinIngesteld: boolean
  /** Geblokkeerd tot (ISO), of null. Geldt ook voor de badge. */
  geblokkeerdTot: string | null
}

export interface KioskOperatorSessieDTO {
  employeeId: string
  naam: string
  /** Het token voor de `x-kiosk-operator`-header (server-side, gaat in een httpOnly-cookie). */
  token: string
  /** ISO. */
  verlooptOp: string
}

export interface KioskContextOperatorDTO {
  employeeId: string
  naam: string
  /** ISO, of null bij een persoonsaccount (die is zelf de operator). */
  verlooptOp: string | null
  /** Zelf ingelogd op de kiosk-URL: geen PIN en geen wissel. */
  isEigenAccount: boolean
}

export interface KioskContextDTO {
  accountType: AccountType
  profiel: KioskProfiel
  accountNaam: string
  locatie: { id: string; naam: string } | null
  operator: KioskContextOperatorDTO | null
  inactiviteitSeconden: number
  pin: { minLengte: number; maxLengte: number }
  /** De tegels die deze operator ziet. De API blijft de poort: dit is alleen de weergave. */
  tegels: KioskTegel[]
}

export interface KioskMedewerkerDTO {
  id: string
  naam: string
  afdeling: string | null
  /** Same-origin proxy-bouwsteen: of er een foto is (de client bouwt het pad zelf). */
  heeftFoto: boolean
}

/** Eén exemplaar op de tablet: alles om mee te boeken, geen aanschafbedrag. */
export interface KioskUniekDTO {
  id: string
  naam: string
  categorie: string
  status: string
  serienummer: string | null
  imei: string | null
  locatie: string | null
  houder: string | null
  houderId: string | null
  set: { dragerId: string; setStatus: string; pakketNaam: string | null } | null
  drager: { id: string; naam: string } | null
}

export interface KioskMedewerkerBezitDTO {
  middelType: string
  middelId: string
  omschrijving: string
  kenmerk: string | null
  aantal: number
  /** ISO. */
  uitgegevenOp: string | null
  /** Koffer of set: de drager (inname als geheel). */
  dragerId: string | null
}

export interface KioskArtikelRijDTO {
  id: string
  artikelNummer: string
  naam: string
  eenheid: string | null
  maat: string | null
  traceerbaar: boolean
  totaalSaldo: number
  fotoAanwezig: boolean
}

export interface KioskArtikelSaldoDTO {
  locatieId: string
  locatieNaam: string
  aantal: number
  vaklocatie: string | null
}

export interface KioskArtikelExemplaarDTO {
  id: string
  label: string
  status: string
  locatieNaam: string | null
  houder: string | null
}

export interface KioskArtikelDetailDTO extends KioskArtikelRijDTO {
  minimumVoorraad: number
  saldi: KioskArtikelSaldoDTO[]
  /** Alleen bij een traceerbaar artikel: de exemplaren, zonder inkoop- of aanschafprijs. */
  exemplaren: KioskArtikelExemplaarDTO[]
  /** Waren er meer exemplaren dan getoond? */
  exemplarenAfgekapt: boolean
}

export interface KioskInkooporderRijDTO {
  id: string
  poNummer: string
  leverancier: string | null
  /** ISO. */
  verwachtDatum: string | null
  aantalRegels: number
  aantalOpen: number
}

export interface KioskInkooporderRegelDTO {
  id: string
  omschrijving: string
  artikelNummer: string | null
  voorraadArtikelId: string | null
  besteld: number
  alOntvangen: number
  open: number
  traceerbaar: boolean
}

export interface KioskInkooporderDetailDTO extends KioskInkooporderRijDTO {
  regels: KioskInkooporderRegelDTO[]
}

export type KioskTeDoenSoort = 'UITRUSTING_ONTBREKEND' | 'RETOURPLICHT' | 'BESTELADVIES' | 'BRUIKLEEN_PRINTEN'

export interface KioskTeDoenItemDTO {
  soort: KioskTeDoenSoort
  id: string
  titel: string
  detail: string | null
  /** ISO. */
  moment: string | null
  /** Bruikleen: het document om te printen via de kiosk-proxy. */
  documentId: string | null
  /** De medewerker om mee verder te gaan (uitgeven of innemen); null bij een besteladvies. */
  employeeId: string | null
}

export interface KioskTeDoenDTO {
  items: KioskTeDoenItemDTO[]
  /** Per soort het echte aantal; `items` is per soort begrensd. */
  aantallen: Record<KioskTeDoenSoort, number>
  /** De soorten die deze kijker mag zien; een soort zonder recht staat er niet in (geen "0"). */
  soorten: KioskTeDoenSoort[]
  /** Soorten waarvan de bron zelf een plafond raakte: het aantal is dan een ondergrens. */
  afgekapt: KioskTeDoenSoort[]
}

export interface KioskAccountDTO {
  id: string
  naam: string | null
  email: string
  profiel: KioskProfiel | null
  isActive: boolean
  locatieId: string | null
  locatieNaam: string | null
  /** ISO. */
  lastLoginAt: string | null
  /** ISO: de laatste regel in de kiosk-audit van dit account. */
  laatsteActiviteitOp: string | null
  /** Geblokkeerd tot (ISO) na te veel foute wachtwoorden, of null. */
  geblokkeerdTot: string | null
  heeftWachtwoord: boolean
}

export interface KioskInstellingenDTO {
  inactiviteitSeconden: number
  pinMinLengte: number
  pinMaxLengte: number
  pinMaxPogingen: number
  pinBlokkadeMinuten: number
  operatorUserIds: string[]
  correctieUserIds: string[]
  tellerUserIds: string[]
  /** Namen bij de lijsten, ook voor wie niet (meer) in de keuzelijst staat. */
  namen: Record<string, string>
  /** Mag deze gebruiker de instellingen en accounts wijzigen (ADMIN)? */
  magWijzigen: boolean
}

/** Eén operator in het beheer: PIN- en passtatus, nooit de PIN of het token zelf. */
export interface KioskOperatorBeheerDTO {
  employeeId: string
  userId: string
  naam: string
  pinIngesteld: boolean
  heeftBadge: boolean
  /** ISO, of null. */
  geblokkeerdTot: string | null
}

export interface KioskAuditRegelDTO {
  id: string
  actie: string
  kioskAccount: string | null
  operator: string | null
  actor: string | null
  deviceLabel: string | null
  detail: unknown
  /** ISO. */
  createdAt: string
}

export interface MagazijnPinStatusDTO {
  /** Staat deze medewerker in de doelgroep Magazijn-operators? Anders is er niets in te stellen. */
  isOperator: boolean
  pinIngesteld: boolean
  minLengte: number
  maxLengte: number
  /** ISO, of null. */
  geblokkeerdTot: string | null
}

// ─── Schema's ────────────────────────────────────────────────────────────────

/** Een PIN: alleen cijfers; de lengte toetst de API tegen de instellingen. */
export const kioskPinSchema = z.string().regex(/^\d{4,8}$/, 'Een PIN bestaat uit cijfers.')

/** Inloggen als operator: tegel plus PIN, of de QR van de medewerkerspas. */
export const kioskOperatorLoginSchema = z
  .object({
    employeeId: idParam('employeeId').optional(),
    pin: kioskPinSchema.optional(),
    badgeToken: z.string().trim().min(8).max(200).optional(),
    deviceLabel: z.string().trim().max(80).nullable().optional(),
    /** De vorige operator wisselde handmatig: voor de audit. */
    wissel: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.badgeToken) return
    if (!v.employeeId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['employeeId'], message: 'Kies eerst wie je bent.' })
    }
    if (!v.pin) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pin'], message: 'Voer je PIN in.' })
    }
  })
export type KioskOperatorLoginInput = z.infer<typeof kioskOperatorLoginSchema>

export const kioskOperatorLockSchema = z.object({
  reden: z.enum(['INACTIEF', 'WISSEL', 'HANDMATIG']).default('HANDMATIG'),
  deviceLabel: z.string().trim().max(80).nullable().optional(),
})
export type KioskOperatorLockInput = z.infer<typeof kioskOperatorLockSchema>

export const kioskMedewerkersQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(60).default(24),
})

export const kioskArtikelenQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(60).default(24),
  /** Alleen artikelen met losse voorraad (geen exemplaren per stuk). */
  alleenBulk: queryBoolean().optional(),
})

export const kioskInkoopordersQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(60).default(24),
})

export const kioskAuditQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(50),
  kioskUserId: idParam('kioskUserId').optional(),
})

/** Wachtwoord van een kioskaccount: lang genoeg voor een gedeeld apparaat. */
export const kioskWachtwoordSchema = z
  .string()
  .min(12, 'Gebruik minimaal 12 tekens voor het wachtwoord van de tablet.')
  .max(128)

export const kioskAccountCreateSchema = z.object({
  naam: z.string().trim().min(2, 'Geef het account een naam.').max(120),
  email: z.string().trim().toLowerCase().email('Vul een geldig e-mailadres in.').max(254),
  profiel: z.enum(KIOSK_PROFIELEN).default('MAGAZIJN'),
  locatieId: idParam('locatieId').nullable().optional(),
  wachtwoord: kioskWachtwoordSchema,
})
export type KioskAccountCreateInput = z.infer<typeof kioskAccountCreateSchema>

export const kioskAccountPatchSchema = z.object({
  naam: z.string().trim().min(2).max(120).optional(),
  locatieId: idParam('locatieId').nullable().optional(),
  isActive: z.boolean().optional(),
})
export type KioskAccountPatchInput = z.infer<typeof kioskAccountPatchSchema>

export const kioskWachtwoordZetSchema = z.object({ wachtwoord: kioskWachtwoordSchema })

/** Een bestaand (bv. Entra-)account als kiosk markeren, de A1-variant. */
export const kioskAccountMarkeerSchema = z.object({
  email: z.string().trim().toLowerCase().email('Vul een geldig e-mailadres in.').max(254),
  profiel: z.enum(KIOSK_PROFIELEN).default('MAGAZIJN'),
  locatieId: idParam('locatieId').nullable().optional(),
})
export type KioskAccountMarkeerInput = z.infer<typeof kioskAccountMarkeerSchema>

export const kioskInstellingenSchema = z
  .object({
    inactiviteitSeconden: z.number().int().min(30, 'Minimaal 30 seconden.').max(900, 'Maximaal 15 minuten.'),
    pinMinLengte: z.number().int().min(4).max(8),
    pinMaxLengte: z.number().int().min(4).max(8),
    pinMaxPogingen: z.number().int().min(3).max(10),
    pinBlokkadeMinuten: z.number().int().min(1).max(1440),
    operatorUserIds: z.array(idParam('operatorUserIds')).max(200),
    correctieUserIds: z.array(idParam('correctieUserIds')).max(200),
    tellerUserIds: z.array(idParam('tellerUserIds')).max(200),
  })
  .superRefine((v, ctx) => {
    if (v.pinMinLengte > v.pinMaxLengte) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pinMaxLengte'],
        message: 'De maximale lengte mag niet kleiner zijn dan de minimale.',
      })
    }
  })
export type KioskInstellingenInput = z.infer<typeof kioskInstellingenSchema>

/** De eigen PIN zetten in de portal; de API toetst lengte en herkenbare reeksen. */
export const magazijnPinZetSchema = z.object({
  pin: kioskPinSchema,
  /** Staat er al een PIN, dan is de huidige verplicht. */
  huidigePin: kioskPinSchema.optional(),
})
export type MagazijnPinZetInput = z.infer<typeof magazijnPinZetSchema>

/**
 * Een PIN die iedereen raadt: allemaal dezelfde cijfers, of een oplopende of aflopende
 * reeks. Puur, zodat de portal hem vóór het opslaan al kan tonen.
 */
export function isZwakkePin(pin: string): boolean {
  if (/^(\d)\1+$/.test(pin)) return true
  const cijfers = pin.split('').map(Number)
  const opl = cijfers.every((c, i) => i === 0 || c === ((cijfers[i - 1] ?? 0) + 1) % 10)
  const afl = cijfers.every((c, i) => i === 0 || c === ((cijfers[i - 1] ?? 0) + 9) % 10)
  return opl || afl
}
